import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, ArrowRight, CheckCircle, Building2, ThermometerSun, Lightbulb, Shield, TrendingDown, Banknote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const CaseStudyExeterOfficeG2A = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Commercial EPC Case Study: Period Office Building G to A Transformation in Exeter",
    "author": { "@type": "Organization", "name": "Energy Performance Direct" },
    "publisher": { "@type": "Organization", "name": "Energy Performance Direct", "url": "https://energyperformancedirect.co.uk" },
    "description": "Real case study showing how a 364.9 sq m period office building in Exeter city centre was transformed from EPC Band G (189) to Band A (25) through staged improvements including roof insulation, wall insulation, LED lighting, AC heating and secondary glazing."
  };

  const options = [
    {
      band: 'E', score: 116, emissions: 63.7, energy: 322.1, color: 'amber',
      measures: ['Improve roof insulation to meet current building regulations of 0.13 W/m2K or better'],
      note: 'Roof insulation alone is sufficient to achieve MEES compliance. This is the minimum investment required to legally let the property.'
    },
    {
      band: 'D', score: 89, emissions: 48.7, energy: 240.33, color: 'yellow',
      measures: [
        'Improve roof insulation to 0.13 W/m2K or better',
        'Insulate external walls internally to a U-value of 0.5 W/m2K or better',
        'Add secondary glazing to all windows',
        'Replace all fixed lighting with LED panels at minimum 90 lumens per circuit watt with 100% light output ratio'
      ],
      note: 'A comprehensive fabric-first and lighting approach that significantly reduces both heating demand and electricity consumption.'
    },
    {
      band: 'C', score: 60, emissions: 32.7, energy: 230.76, color: 'green',
      measures: [
        'Remove the gas boiler and replace with split or multi-split AC units (COP 3, EER 3 or better) with local time and temperature controls',
        'Add an under-sink electric water heater to the first floor'
      ],
      note: 'Switching from gas to efficient electric heating via heat pump technology delivers a major rating improvement. This option focuses purely on the heating system change.'
    },
    {
      band: 'B', score: 38, emissions: 20.9, energy: 144.81, color: 'blue',
      measures: [
        'Replace the gas boiler with split or multi-split AC units (COP 3, EER 3 or better) with local time and temperature controls',
        'Add an under-sink electric water heater to the first floor',
        'Improve roof insulation to 0.13 W/m2K or better'
      ],
      note: 'Combining the heating system upgrade with roof insulation delivers Band B. This represents strong energy performance and positions the building well for potential future MEES tightening.'
    },
    {
      band: 'A', score: 25, emissions: 13.5, energy: 93.61, color: 'emerald',
      measures: [
        'Replace the gas boiler with split or multi-split AC units (COP 3, EER 3 or better) with local time and temperature controls',
        'Add point-of-use electric water heaters throughout',
        'Improve roof insulation to 0.13 W/m2K or better',
        'Insulate external walls internally to a U-value of 0.5 W/m2K or better',
        'Add secondary glazing to all windows',
        'Replace all fixed lighting with LED panels at minimum 90 lumens per circuit watt with 100% light output ratio'
      ],
      note: 'The complete transformation. All measures combined achieve the highest possible rating band, reducing emissions by 87% and energy use by 84% compared to baseline.'
    }
  ];

  const bandColors = {
    E: 'border-amber-500 bg-amber-50',
    D: 'border-yellow-500 bg-yellow-50',
    C: 'border-green-500 bg-green-50',
    B: 'border-blue-500 bg-blue-50',
    A: 'border-emerald-500 bg-emerald-50'
  };

  const bandTextColors = {
    E: 'text-amber-700',
    D: 'text-yellow-700',
    C: 'text-green-700',
    B: 'text-blue-700',
    A: 'text-emerald-700'
  };

  return (
    <>
      <Helmet>
        <title>Commercial EPC Case Study | Period Office G to A Transformation Exeter | Energy Performance Direct</title>
        <meta name="description" content="Real case study: How a 364.9 sq m period office building in Exeter achieved Band A from a G189 baseline. 5-option improvement pathway with financial payback analysis, MEES compliance and full SBEM modelling." />
        <meta name="keywords" content="commercial EPC case study Exeter, office EPC G to A, MEES compliance case study, commercial energy assessment Devon, period building EPC improvement, SBEM modelling Exeter, commercial EPC advice report" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/case-study-exeter-office-g-to-a" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <Navbar />
      <Breadcrumbs items={[
        { label: 'Industrial EPC', path: '/industrial-epc' },
        { label: 'Case Study: G to A Office', path: '/case-study-exeter-office-g-to-a' }
      ]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-900 via-green-800 to-emerald-900 text-white py-16" data-testid="case-study-g2a-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-300 px-4 py-2 rounded-full text-sm mb-6">
              <Building2 className="w-4 h-4" />
              Real Case Study
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Period Office Building: G189 to A25 Transformation in Exeter
            </h1>
            <p className="text-lg text-emerald-200 mb-4">
              How we provided a five-option improvement roadmap for a period office building in Exeter city centre, enabling the client to transform their property from the lowest EPC band to the highest.
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-emerald-300 mt-6">
              <span className="bg-gray-800 px-3 py-1 rounded-full">364.9 sq m</span>
              <span className="bg-gray-800 px-3 py-1 rounded-full">Exeter City Centre</span>
              <span className="bg-gray-800 px-3 py-1 rounded-full">1st & 2nd Floor Offices</span>
              <span className="bg-gray-800 px-3 py-1 rounded-full">G to A Transformation</span>
            </div>

            {/* Rating Journey */}
            <div className="flex items-center gap-2 mt-10 flex-wrap">
              <div className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold text-xl">G189</div>
              <ArrowRight className="w-5 h-5 text-emerald-300" />
              <div className="bg-amber-500 text-white px-4 py-2 rounded-lg font-bold text-xl">E116</div>
              <ArrowRight className="w-5 h-5 text-emerald-300" />
              <div className="bg-yellow-500 text-white px-4 py-2 rounded-lg font-bold text-xl">D89</div>
              <ArrowRight className="w-5 h-5 text-emerald-300" />
              <div className="bg-green-500 text-white px-4 py-2 rounded-lg font-bold text-xl">C60</div>
              <ArrowRight className="w-5 h-5 text-emerald-300" />
              <div className="bg-blue-500 text-white px-4 py-2 rounded-lg font-bold text-xl">B38</div>
              <ArrowRight className="w-5 h-5 text-emerald-300" />
              <div className="bg-emerald-500 text-white px-4 py-2 rounded-lg font-bold text-xl">A25</div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p>
                Energy Performance Direct was commissioned to carry out a full commercial energy assessment and provide a detailed Energy Advice Report for a period office building in Exeter city centre. The property comprises first and second floor office accommodation totalling 364.9 square metres, heated by a natural gas boiler with natural ventilation.
              </p>
              <p>
                The baseline assessment revealed a G189 rating, placing the building firmly in the lowest EPC band. With <Link to="/mees-commercial-buildings" className="text-green-600 hover:underline">MEES regulations</Link> requiring a minimum E rating for all commercial rental properties, the client needed a clear pathway to compliance along with options for achieving higher ratings to future-proof the property.
              </p>
              <p>
                We provided five distinct improvement options, each building progressively on the previous, taking the building from G through E, D, C, B and ultimately to Band A. This gave the client the flexibility to choose the level of investment appropriate for their circumstances while understanding exactly what each option would deliver.
              </p>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
              <Card className="border-none bg-red-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-red-600">G189</p>
                  <p className="text-sm text-gray-600 mt-1">Baseline Rating</p>
                </CardContent>
              </Card>
              <Card className="border-none bg-emerald-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-600">A25</p>
                  <p className="text-sm text-gray-600 mt-1">Achieved Rating</p>
                </CardContent>
              </Card>
              <Card className="border-none bg-gray-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-gray-900">87%</p>
                  <p className="text-sm text-gray-600 mt-1">CO2 Reduction</p>
                </CardContent>
              </Card>
              <Card className="border-none bg-blue-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">84%</p>
                  <p className="text-sm text-gray-600 mt-1">Energy Reduction</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Baseline Analysis */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Baseline Analysis</h2>
            <p className="text-gray-700 mb-8">
              The building presented several common challenges found in period commercial properties in Exeter and similar South West towns:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900">Key Challenges</h3>
                {[
                  { icon: ThermometerSun, title: 'Gas boiler heating', desc: 'Natural gas boiler serving the entire building. Gas heating in commercial buildings carries a significant carbon penalty in SBEM calculations.' },
                  { icon: Shield, title: 'Poor insulation', desc: 'Uninsulated roof, walls and floors typical of a period building. Heat loss through the building fabric was substantial.' },
                  { icon: Building2, title: 'Single and double glazing', desc: 'Original windows with high U-values contributing to both heat loss and solar gain issues.' },
                  { icon: Lightbulb, title: 'Older lighting', desc: 'Fixed lighting throughout was below modern efficiency standards, contributing to unnecessary electricity consumption.' }
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 p-3 bg-white rounded-lg shadow-sm">
                    <item.icon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-sm text-gray-900">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Baseline Figures</h3>
                <Card className="border-red-200">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b">
                      <span className="text-gray-700">EPC Rating</span>
                      <span className="font-bold text-red-600 text-xl">G (189)</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b">
                      <span className="text-gray-700">CO2 Emissions</span>
                      <span className="font-semibold">103.8 kgCO2/m2/yr</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b">
                      <span className="text-gray-700">Total Energy Use</span>
                      <span className="font-semibold">512.72 kWh/m2/yr</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b">
                      <span className="text-gray-700">Primary Energy</span>
                      <span className="font-semibold">598.4 kWh/m2/yr</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b">
                      <span className="text-gray-700">Energy Split</span>
                      <span className="font-semibold">88% Gas / 12% Electric</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">If Newly Built</span>
                      <span className="font-semibold text-green-600">22</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Five Options */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Five-Option Improvement Roadmap</h2>
            <p className="text-gray-300 mb-10">
              We provided five progressive improvement options, each with precise <Link to="/sbem-explained" className="text-green-400 hover:underline">SBEM-modelled</Link> figures. This approach gives the building owner complete flexibility to choose the right level of investment while understanding exactly what each option achieves.
            </p>
            <div className="space-y-6">
              {options.map((opt, i) => (
                <div key={i} className={`border-l-4 pl-6 ${opt.band === 'E' ? 'border-amber-500' : opt.band === 'D' ? 'border-yellow-500' : opt.band === 'C' ? 'border-green-500' : opt.band === 'B' ? 'border-blue-500' : 'border-emerald-500'}`}>
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className={`text-sm font-bold px-3 py-1 rounded-full ${opt.band === 'E' ? 'bg-amber-600' : opt.band === 'D' ? 'bg-yellow-600' : opt.band === 'C' ? 'bg-green-600' : opt.band === 'B' ? 'bg-blue-600' : 'bg-emerald-600'} text-white`}>
                      Option {i + 1}
                    </span>
                    <span className={`font-bold text-lg ${opt.band === 'E' ? 'text-amber-400' : opt.band === 'D' ? 'text-yellow-400' : opt.band === 'C' ? 'text-green-400' : opt.band === 'B' ? 'text-blue-400' : 'text-emerald-400'}`}>
                      Band {opt.band} ({opt.score})
                    </span>
                    <span className="text-sm text-gray-400">
                      {opt.emissions} kgCO2/m2/yr | {opt.energy} kWh/m2/yr
                    </span>
                  </div>
                  <ul className="space-y-2 mb-3">
                    {opt.measures.map((m, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${opt.band === 'E' ? 'text-amber-400' : opt.band === 'D' ? 'text-yellow-400' : opt.band === 'C' ? 'text-green-400' : opt.band === 'B' ? 'text-blue-400' : 'text-emerald-400'}`} />
                        <span className="text-gray-300 text-sm">{m}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-gray-500 italic">{opt.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Financial Analysis */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Financial Payback Analysis</h2>
            <div className="prose prose-lg max-w-none text-gray-700 mb-8">
              <p>
                As part of the MEES compliance assessment, we conducted a detailed financial payback analysis for the roof insulation measure. This calculation is required to determine whether a MEES exemption can be claimed on the basis that improvements fail a 7-year payback test.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-green-200">
                <CardContent className="p-6">
                  <Banknote className="w-8 h-8 text-green-600 mb-3" />
                  <h3 className="font-semibold text-lg mb-4">Energy Savings (Roof Insulation)</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between pb-2 border-b">
                      <span className="text-gray-600">Energy saved annually</span>
                      <span className="font-semibold">190.62 kWh/m2/yr</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b">
                      <span className="text-gray-600">Total building savings (7yr)</span>
                      <span className="font-semibold">486,888 kWh</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b">
                      <span className="text-gray-600">Gas savings (7yr)</span>
                      <span className="font-semibold text-green-700">£48,759</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b">
                      <span className="text-gray-600">Electricity savings (7yr)</span>
                      <span className="font-semibold text-green-700">£22,225</span>
                    </div>
                    <div className="flex justify-between pt-1">
                      <span className="font-semibold text-gray-900">Total 7-year savings</span>
                      <span className="font-bold text-green-700 text-lg">£70,984</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-blue-200">
                <CardContent className="p-6">
                  <TrendingDown className="w-8 h-8 text-blue-600 mb-3" />
                  <h3 className="font-semibold text-lg mb-4">Cost vs Savings</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between pb-2 border-b">
                      <span className="text-gray-600">Improvement cost</span>
                      <span className="font-semibold">£51,172</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b">
                      <span className="text-gray-600">7-year savings</span>
                      <span className="font-semibold text-green-700">£70,984</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b">
                      <span className="text-gray-600">Net benefit (7yr)</span>
                      <span className="font-semibold text-green-700">+£19,812</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b">
                      <span className="text-gray-600">BoE base rate used</span>
                      <span className="font-semibold">5.25%</span>
                    </div>
                    <div className="flex justify-between pt-1">
                      <span className="font-semibold text-gray-900">MEES Exemption?</span>
                      <span className="font-bold text-red-600">Does not qualify</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">The 7-year savings exceed the improvement cost, meaning the measure passes the payback test and a MEES exemption cannot be claimed. The works must be carried out.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Important Advice */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Professional Guidance Notes</h2>
            <div className="space-y-4">
              {[
                { title: 'Comprehensive insulation', text: 'Insulation should be installed comprehensively with no gaps. Partially insulating a building can cause thermal bridging, condensation and damp issues. This is particularly important in period buildings where existing construction may be moisture-sensitive.' },
                { title: 'Airtightness and ventilation', text: 'Making the building airtight will further improve costs, comfort and CO2 output. However, improved airtightness increases potential condensation risk. Passive or mechanical ventilation strategies should be considered. Pressure tests are recommended after insulation installation but before decorating to identify and address any gaps.' },
                { title: 'Future-proofing with Phase 3 electrics', text: 'Phase 3 electrical upgrades are highly recommended to future-proof the building for heat pumps, EV charging, smart appliances and battery installations. As the grid decarbonises, electrically-heated buildings will achieve progressively better EPC ratings.' },
                { title: 'Impartial advice', text: 'All advice provided is based on improved EPC scores and is impartial and independent of any installer or specific products. We always recommend obtaining 3 to 5 like-for-like quotes to gain an accurate understanding of practical and cost implications.' }
              ].map((item, i) => (
                <div key={i} className="p-5 bg-white rounded-lg shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-700 text-sm">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Results Summary */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Transformation Summary</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transformation-table">
                <thead>
                  <tr className="border-b-2">
                    <th className="text-left py-3 px-4">Option</th>
                    <th className="text-center py-3 px-4">Rating</th>
                    <th className="text-center py-3 px-4">CO2 (kgCO2/m2/yr)</th>
                    <th className="text-center py-3 px-4">Energy (kWh/m2/yr)</th>
                    <th className="text-center py-3 px-4">CO2 Reduction</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b bg-red-50">
                    <td className="py-3 px-4 font-semibold">Baseline</td>
                    <td className="text-center py-3 px-4"><span className="bg-red-600 text-white px-2 py-0.5 rounded font-bold">G189</span></td>
                    <td className="text-center py-3 px-4">103.8</td>
                    <td className="text-center py-3 px-4">512.72</td>
                    <td className="text-center py-3 px-4">-</td>
                  </tr>
                  {options.map((opt, i) => (
                    <tr key={i} className={`border-b ${bandColors[opt.band]}`}>
                      <td className="py-3 px-4 font-semibold">Option {i + 1}</td>
                      <td className="text-center py-3 px-4">
                        <span className={`px-2 py-0.5 rounded font-bold ${opt.band === 'E' ? 'bg-amber-500 text-white' : opt.band === 'D' ? 'bg-yellow-500 text-white' : opt.band === 'C' ? 'bg-green-500 text-white' : opt.band === 'B' ? 'bg-blue-500 text-white' : 'bg-emerald-500 text-white'}`}>
                          {opt.band}{opt.score}
                        </span>
                      </td>
                      <td className="text-center py-3 px-4">{opt.emissions}</td>
                      <td className="text-center py-3 px-4">{opt.energy}</td>
                      <td className="text-center py-3 px-4 font-semibold text-green-700">{Math.round((1 - opt.emissions / 103.8) * 100)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Takeaways</h2>
            <div className="space-y-4">
              {[
                'Even period buildings with a G189 baseline can achieve Band A through a well-planned combination of fabric improvements, efficient heating systems, modern lighting and careful specification.',
                'Roof insulation alone can shift a G-rated building to Band E, achieving MEES compliance with a single improvement measure. For landlords under immediate compliance pressure, this is the fastest route.',
                'Replacing a gas boiler with split AC units (heat pump technology) is one of the most impactful measures in SBEM calculations. The switch from gas to efficient electric heating can single-handedly deliver two or more band improvements.',
                'A five-option approach gives building owners genuine flexibility. Not every property requires Band A, and understanding the cost-benefit of each step allows informed investment decisions.',
                'Financial payback analysis is essential for MEES compliance. Where savings exceed improvement costs over seven years, a MEES exemption cannot be claimed and the works must be carried out.',
                'Future-proofing with Phase 3 electrics, ventilation strategies and pressure testing should be considered alongside EPC improvements to protect the investment and avoid condensation issues.'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Services and Guides</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/mees-commercial-buildings" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">MEES Regulations Guide</h3>
                <p className="text-sm text-gray-600">Compliance requirements and penalties</p>
              </Link>
              <Link to="/sbem-explained" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">SBEM Explained</h3>
                <p className="text-sm text-gray-600">How commercial ratings are calculated</p>
              </Link>
              <Link to="/industrial-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Industrial EPCs</h3>
                <p className="text-sm text-gray-600">Warehouse, factory and workshop assessments</p>
              </Link>
              <Link to="/case-study-exeter-office-workshop" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Case Study: Office & Workshop</h3>
                <p className="text-sm text-gray-600">793.5 sq m G-to-A improvement roadmap</p>
              </Link>
              <Link to="/case-study-exeter-workshop-refurbishment" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Case Study: Workshop Refurb</h3>
                <p className="text-sm text-gray-600">Twin units D-to-B refurbishment modelling</p>
              </Link>
              <Link to="/industrial-epc-devon" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Industrial EPCs Devon</h3>
                <p className="text-sm text-gray-600">Devon industrial and commercial assessments</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need a Commercial EPC or Energy Advice Report?</h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Our accredited assessors provide detailed EPC assessments with practical improvement roadmaps. Whether you need MEES compliance advice or a full improvement strategy, we can help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
              <Link to="/booking?service=commercial_advice">
                Request Advice Report Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <a href="tel:01392980077">
                <Phone className="mr-2 w-5 h-5" />
                Call 01392 980077
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default CaseStudyExeterOfficeG2A;
